import type {
  AdminTimelineEntry,
  AdminTimelineKind,
} from "@/types/admin-user";

/**
 * Map backend timeline rows → the frontend `AdminTimelineEntry` contract.
 *
 * The backend (GET /admin/users/{id}/timeline) serves
 * `{source,type,summary,occurredAt,severity,reason,actor:str}`; the dashboard's
 * `mapTimelineEntry` reads `{entryId,kind,tone,actor:object,...}`. Without this
 * bridge a membership `state_change` (e.g. `past_due -> expired`) rendered with a
 * missing React key, a fallback tone/icon, and the transition `reason` dropped.
 */

const SOURCE_TO_KIND: Record<string, AdminTimelineKind> = {
  membership: "membership",
  reclaim: "reclaim",
  support: "support",
  privacy: "consent",
  deletion: "membership",
  admin: "support",
};

const SEVERITY_TO_TONE: Record<string, NonNullable<AdminTimelineEntry["tone"]>> = {
  info: "info",
  warn: "warn",
  crit: "crit",
};

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/** A string actor token ("system"/"stripe"/"scheduler"/an admin id) → actor object. */
function actorObject(actor: unknown): AdminTimelineEntry["actor"] {
  const raw = readString(actor);
  if (!raw) return { type: "system", id: "system", displayName: "System" };
  const lower = raw.toLowerCase();
  const type: AdminTimelineEntry["actor"]["type"] =
    lower === "user"
      ? "user"
      : lower === "system" || lower === "stripe" || lower === "scheduler"
        ? "system"
        : "admin";
  return { type, id: raw, displayName: raw };
}

function normalizeEntry(raw: unknown, index: number): AdminTimelineEntry {
  const r = (raw ?? {}) as Record<string, unknown>;
  const source = readString(r.source) ?? "membership";
  const type = readString(r.type) ?? "event";
  const occurredAt = readString(r.occurredAt) ?? readString(r.occurred_at) ?? "";
  const summary = readString(r.summary) ?? type;
  const severity = readString(r.severity) ?? "info";
  const reason = readString(r.reason);

  return {
    // Stable within a fetch: the backend supplies no id, so key off the
    // source/type/timestamp plus the array index (dedupes same-instant rows).
    entryId: `${source}:${type}:${occurredAt || "na"}:${index}`,
    kind: SOURCE_TO_KIND[source] ?? "membership",
    occurredAt,
    summary,
    tone: SEVERITY_TO_TONE[severity] ?? "info",
    // Surface the transition reason as a meta row so it is never dropped.
    meta: reason ? [{ label: "Reason", value: reason, mono: true }] : undefined,
    actor: actorObject(r.actor),
  };
}

export function normalizeAdminTimeline(data: unknown): AdminTimelineEntry[] {
  if (!Array.isArray(data)) return [];
  return data.map((row, i) => normalizeEntry(row, i));
}
